import { defineStore } from "pinia";
import AbortService from "@rmp/core/services/abortService";
import { SnapshotKeysEnum, SnapshotState, useSnapshotStore } from "@rmp/core/stores/composables/snapshot";
import { FormState, useFormStore } from "@rmp/core/stores/composables/form/useFormStore";
import { AuthAppController } from "@rmp/id/api/auth";
import { ApiLoginPayload, ApiProfile } from "@rmp/api-id";
import HttpNotFoundException from "@rmp/core/exceptions/httpNotFoundException";
import alertService, { AlertKeys } from "@rmp/core/stores/alerts/services/alertService";
import router from "@rmp/id/router";
import { RouteNames } from "@rmp/id/router/routes";
import { useAppStore } from "@rmp/core/stores/appStore";
import i18n from "@rmp/id/plugins/i18n";

import { captchaEnabled } from "@rmp/core/services/captcha";

const abortService = new AbortService();
const authAppController = new AuthAppController(abortService);

const form = useFormStore();
const snapshotStore = useSnapshotStore([
	{ key: SnapshotKeysEnum.LAST_SAVED, fields: ["user"] }
]);

export interface SignInState extends SnapshotState, FormState {
	profiles: ApiProfile[];
	user: ApiLoginPayload;
	captchaToken: string;
}

const getDefaultState = (): SignInState => {
	return {
		...form.getDefaultState(),
		...snapshotStore.getDefaultState(),
		profiles: [],
		captchaToken: "",
		user: {
			login: "",
			password: ""
		}
	};
};

export const useSignInStore = defineStore({
	id: "sign-in",
	state: (): SignInState => getDefaultState(),
	getters: {
		...form.getters,
		...snapshotStore.getters
	},
	actions: {
		...form.actions,
		...snapshotStore.actions,
		async initialize() {
			abortService.initialize();
			
			this.$reset();
			this.setStateSnapshot();
		},
		async save() {
			if(this.formSaving || (captchaEnabled && !this.captchaToken)) return;

			this.formSaving = true;
			const captchaToken = this.captchaToken;
			this.captchaToken = "";

			try {
				const { data: { profiles, redirectUri } } = await authAppController.login(this.user, captchaToken);

				if (!redirectUri || !profiles) throw new Error(i18n.t("errors.loginRedirectUriOrProfilesInvalid").toString());
				
				const { data: { redirectUri: callbackUrl } } = await authAppController.authorize(redirectUri);

				switch (profiles.length) {
					case 0:
					{
						(window as Window).location = callbackUrl;
						break;
					}
					case 1:
					{
						const [profile] = profiles;

						if(!profile.isSignatureRequired) {
							const { data: { redirectUri } } = await authAppController.signInWithProfile({
								profileId: profile.id
							});

							if (!redirectUri) throw new Error(i18n.t("errors.signInWithProfileRedirectUriInvalid").toString());

							const { data: { redirectUri: callbackUrl } } = await authAppController.authorize(redirectUri);
							(window as Window).location = callbackUrl;
						} else {
							this.profiles = profiles;
							
							await router.push({ name: RouteNames.SELECT_PROFILE, query: router.currentRoute.query });
						}
						
						break;
					}
					default:
					{
						this.profiles = profiles;

						await router.push({ name: RouteNames.SELECT_PROFILE, query: router.currentRoute.query });
					}
				}
			} catch (error) {
				if(error instanceof HttpNotFoundException) {
					alertService.addError(AlertKeys.ACCOUNT_NOT_FOUND_ERROR);
				} else {
					useAppStore().handleActionError(error);
				}
			} finally {
				this.captchaToken = "";
				this.formSaving = false;
			}
		}
	}
});
